import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import {
  Confetti,
  UserCircle,
  Receipt,
  FilePdf,
  ArrowRight,
} from "@phosphor-icons/react";

/**
 * One-time welcome dialog for freshly created subcontractors.
 * Shows on first login per user, dismissed via localStorage.
 */
export default function WelcomeDialog({ user, profile }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user || user.role !== "subcontractor") return;
    const key = `heo_welcome_seen_${user.id}`;
    const seen = localStorage.getItem(key);
    const profileComplete =
      profile?.company_name &&
      profile?.bank_name &&
      profile?.account_number &&
      profile?.sort_code;
    // Show if never seen, OR profile is still incomplete (nudge them)
    if (!seen && !profileComplete) {
      setOpen(true);
    }
  }, [user, profile]);

  const dismiss = () => {
    if (user) localStorage.setItem(`heo_welcome_seen_${user.id}`, "1");
    setOpen(false);
  };

  const dismissAndGo = () => {
    dismiss();
    navigate("/settings");
  };

  if (!user || user.role !== "subcontractor") return null;

  const firstName = (user.name || "").split(" ")[0] || "there";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && dismiss()}>
      <DialogContent className="max-w-lg" data-testid="welcome-dialog">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="bg-primary text-primary-foreground p-2.5 rounded-sm">
              <Confetti size={22} weight="fill" />
            </div>
            <div>
              <div className="overline text-primary">Welcome aboard</div>
              <DialogTitle className="font-display text-2xl font-black tracking-tighter mt-0.5">
                Hey {firstName} 👋
              </DialogTitle>
            </div>
          </div>
          <DialogDescription className="text-sm text-foreground/80">
            This is your HEO IT Engineering invoice portal. Submit the days you've worked,
            track payment status, and keep everything in one place.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 my-2">
          <div className="flex items-start gap-3 p-3 border border-border bg-muted/30">
            <div className="bg-primary/10 text-primary p-1.5 rounded-sm shrink-0 mt-0.5">
              <UserCircle size={16} weight="bold" />
            </div>
            <div className="text-sm">
              <div className="font-semibold">1. Fill in your profile</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                Add your trading name, address, and bank details — these appear on every invoice you issue.
              </div>
            </div>
          </div>
          <div className="flex items-start gap-3 p-3 border border-border bg-muted/30">
            <div className="bg-primary/10 text-primary p-1.5 rounded-sm shrink-0 mt-0.5">
              <Receipt size={16} weight="bold" />
            </div>
            <div className="text-sm">
              <div className="font-semibold">2. Submit a timesheet</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                New Invoice → add days worked, hours, and your rate — then hit "Submit to HEO".
              </div>
            </div>
          </div>
          <div className="flex items-start gap-3 p-3 border border-border bg-muted/30">
            <div className="bg-primary/10 text-primary p-1.5 rounded-sm shrink-0 mt-0.5">
              <FilePdf size={16} weight="bold" />
            </div>
            <div className="text-sm">
              <div className="font-semibold">3. Download your PDF anytime</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                Every submitted invoice has a one-click PDF download for your records.
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={dismiss}
            className="rounded-sm"
            data-testid="welcome-later-btn"
          >
            Later
          </Button>
          <Button
            onClick={dismissAndGo}
            className="rounded-sm"
            data-testid="welcome-setup-btn"
          >
            Set up my profile
            <ArrowRight size={16} weight="bold" className="ml-2" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
